import { useEffect, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useSession } from "@/lib/session";
import { CallProvider, useCalls } from "@/lib/calls";
import { SoftphoneProvider } from "@/lib/softphone";
import { TopBar } from "@/components/TopBar";

const LINKS = [
  { href: "/app", label: "Dashboard", icon: "grid", roles: ["owner", "admin", "supervisor", "agent"] },
  { href: "/app/live", label: "Live calls", icon: "live", roles: ["owner", "admin", "supervisor", "agent"] },
  { href: "/app/users", label: "Users", icon: "users", roles: ["owner", "admin"] },
  { href: "/app/extensions", label: "Extensions", icon: "ext", roles: ["owner", "admin", "supervisor", "agent"] },
  { href: "/app/inbound", label: "Inbound", icon: "inbound", roles: ["owner", "admin", "supervisor"] },
  { href: "/app/cdr", label: "CDR", icon: "list", roles: ["owner", "admin", "supervisor"] },
  { href: "/app/bulk", label: "Bulk calling", icon: "bulk", roles: ["owner", "admin", "supervisor"] },
  { href: "/app/contacts", label: "Contacts", icon: "book", roles: ["owner", "admin", "supervisor", "agent"] },
  { href: "/app/softphone", label: "Softphone", icon: "phone", roles: ["owner", "admin", "supervisor", "agent"] },
  { href: "/app/apps", label: "MQTT / PBX", icon: "pbx", roles: ["owner", "admin"] },
  { href: "/app/developers", label: "Developers", icon: "docs", roles: ["owner", "admin", "supervisor", "agent"] },
  { href: "/app/profile", label: "Profile", icon: "profile", roles: ["owner", "admin", "supervisor", "agent"] },
];

function Icon({ name }: { name: string }) {
  const common = {
    width: 18,
    height: 18,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.8,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
  };
  if (name === "grid") {
    return (
      <svg {...common}>
        <rect x="3" y="3" width="7" height="7" rx="1.5" />
        <rect x="14" y="3" width="7" height="7" rx="1.5" />
        <rect x="3" y="14" width="7" height="7" rx="1.5" />
        <rect x="14" y="14" width="7" height="7" rx="1.5" />
      </svg>
    );
  }
  if (name === "phone") {
    return (
      <svg {...common}>
        <path d="M8 3h3l1 4-2 1a12 12 0 0 0 6 6l1-2 4 1v3a2 2 0 0 1-2 2A16 16 0 0 1 5 7a2 2 0 0 1 3-4z" />
      </svg>
    );
  }
  if (name === "live") {
    return (
      <svg {...common}>
        <circle cx="12" cy="12" r="3" />
        <path d="M5 12a7 7 0 0 1 14 0M2 12a10 10 0 0 1 20 0" />
      </svg>
    );
  }
  if (name === "list") {
    return (
      <svg {...common}>
        <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />
      </svg>
    );
  }
  if (name === "bulk") {
    return (
      <svg {...common}>
        <path d="M4 6h12M4 12h16M4 18h10" />
        <path d="M18 4v6M15 7h6" />
      </svg>
    );
  }
  if (name === "users") {
    return (
      <svg {...common}>
        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="3" />
        <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    );
  }
  if (name === "ext") {
    return (
      <svg {...common}>
        <path d="M5 9h14M5 15h14M10 4 8 20M16 4l-2 16" />
      </svg>
    );
  }
  if (name === "pbx") {
    return (
      <svg {...common}>
        <rect x="3" y="4" width="18" height="14" rx="2" />
        <path d="M7 8h.01M11 8h.01M15 8h.01M7 12h10" />
      </svg>
    );
  }
  if (name === "book") {
    return (
      <svg {...common}>
        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
      </svg>
    );
  }
  if (name === "inbound") {
    return (
      <svg {...common}>
        <path d="M22 2 11 13" />
        <path d="M22 2h-7M22 2v7" />
        <path d="M12 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-6" />
      </svg>
    );
  }
  if (name === "voice") {
    return (
      <svg {...common}>
        <path d="M12 3v18" />
        <path d="M8 8v8" />
        <path d="M16 8v8" />
        <path d="M4 10v4" />
        <path d="M20 10v4" />
      </svg>
    );
  }
  if (name === "docs") {
    return (
      <svg {...common}>
        <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
        <path d="M14 3v5h5" />
        <path d="M9 13h6M9 17h6" />
      </svg>
    );
  }
  if (name === "profile") {
    return (
      <svg {...common}>
        <circle cx="12" cy="8" r="3.2" />
        <path d="M5 19c1.5-3.2 3.8-5 7-5s5.5 1.8 7 5" />
      </svg>
    );
  }
  if (name === "bill") {
    return (
      <svg {...common}>
        <rect x="3" y="5" width="18" height="14" rx="2" />
        <path d="M3 10h18M8 15h3" />
      </svg>
    );
  }
  if (name === "orgs") {
    return (
      <svg {...common}>
        <path d="M3 21V8l6-4 6 4v13" />
        <path d="M9 21v-6h6v6" />
        <path d="M15 10h6v11" />
      </svg>
    );
  }
  return (
    <svg {...common}>
      <path d="M12 3v18M8 7h8M6 12h12M8 17h8" />
    </svg>
  );
}

function initials(name: string) {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("");
}

function timeAgo(iso: string) {
  const s = Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 1000));
  if (!Number.isFinite(s) || s < 45) return "just now";
  if (s < 3600) return `${Math.max(1, Math.floor(s / 60))} min ago`;
  if (s < 86400) {
    const h = Math.floor(s / 3600);
    return `${h} hour${h === 1 ? "" : "s"} ago`;
  }
  const d = Math.floor(s / 86400);
  if (d < 7) return `${d} day${d === 1 ? "" : "s"} ago`;
  return new Date(iso).toLocaleDateString();
}

function formatCallClock(iso: string) {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return "";
  return d.toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function fmtTalk(sec: number) {
  if (!sec) return "No talk time";
  const m = Math.floor(sec / 60);
  const r = sec % 60;
  return m ? `${m}m ${r}s` : `${r}s`;
}

function lastLabel(state: string, cause: string) {
  if (/NO_ANSWER|NO_CHANNEL|CANCEL|ORIGINATOR_CANCEL|no_answer|busy|miss/i.test(cause || state)) {
    return "Missed";
  }
  if (/answered|completed|hungup|ended/i.test(state)) return "Completed";
  return state || "Ended";
}

function fmtDurationLive(sec: number | string) {
  const n = Math.max(0, Number(sec) || 0);
  const m = Math.floor(n / 60);
  const r = n % 60;
  return `${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
}

function navActive(path: string, href: string) {
  if (href === "/app") return path === "/app";
  return path === href || path.startsWith(`${href}/`);
}

function Shell() {
  const { user, org, orgName, loading, logout } = useSession();
  const { pathname: path } = useLocation();
  const navigate = useNavigate();
  const calls = useCalls();
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const n = params.get("dial");
    if (n) sessionStorage.setItem("ipbs_pending_dial", n.replace(/[^\d+*#]/g, ""));
    if (!loading && !user) navigate("/", { replace: true });
  }, [loading, user, navigate]);

  useEffect(() => {
    setCollapsed(localStorage.getItem("ipbs_sidebar") === "1");
  }, []);

  function toggleSide() {
    setCollapsed((c) => {
      const next = !c;
      localStorage.setItem("ipbs_sidebar", next ? "1" : "0");
      return next;
    });
  }

  if (loading || !user) {
    return (
      <div className="boot">
        <p className="brand">
          Ne<span>ron</span>
        </p>
        <p className="muted">Opening console…</p>
      </div>
    );
  }

  return (
    <div className={`app${collapsed ? " collapsed" : ""}`}>
      <aside className="side">
        <div className="side-brand">
          <div className="side-top">
            <p className="brand">
              Ne<span>ron</span>
            </p>
            <button
              className="collapse-btn"
              type="button"
              onClick={toggleSide}
              title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
              aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              {collapsed ? "»" : "«"}
            </button>
          </div>
          <div className="side-copy">
            <span className="side-tag">{org?.planLabel || "Workspace"}</span>
            <p className="org-name">{orgName}</p>
          </div>
        </div>
        <nav className="nav">
          {LINKS.filter((l) => {
            if (!l.roles.includes(user.role)) return false;
            if ("platformOnly" in l && l.platformOnly) {
              if (user.role !== "super_admin" && !org?.isPlatform) return false;
            }
            return true;
          }).map((l) => (
            <Link
              key={l.href}
              to={l.href}
              className={navActive(path, l.href) ? "active" : ""}
              title={l.label}
            >
              <Icon name={l.icon} />
              <span className="nav-label">{l.label}</span>
            </Link>
          ))}
        </nav>
        <div className="who-card">
          <div className="who-row">
            <div className="avatar" title={user.name}>
              {initials(user.name)}
            </div>
            <div className="who-copy">
              <b>
                <Link to="/app/profile">{user.name}</Link>
              </b>
              <div className="who-pills">
                <span className="pill">{user.role}</span>
                <span className={`pill ${user.extension ? "live" : "dead"}`}>
                  ext {user.extension || "unmapped"}
                </span>
                <span className={`pill ${user.phoneMode === "sip" ? "idle" : "live"}`}>
                  {user.phoneMode === "sip" ? "Softphone" : "Hard phone"}
                </span>
              </div>
            </div>
          </div>
          <button className="btn ghost btn-block sign-out" onClick={logout} title="Sign out">
            Sign out
          </button>
        </div>
      </aside>
      <div className="stage">
        <TopBar />
        <div className="main"><Outlet /></div>
        {calls.myCall && !calls.popup && (
          <div className="callbar">
            <div>
              <span className={`pill ${calls.myCall.state === "answered" ? "live" : "ring"}`}>
                {calls.myCall.state}
              </span>{" "}
              {calls.myCall.direction === "inbound" ? "Incoming" : "Outgoing"} ·{" "}
              {calls.myCall.from} → {calls.myCall.to}
              <span className="muted"> · {String(calls.myCall.duration)}s</span>
            </div>
            <div className="btn-row">
              <button className="btn danger" onClick={() => void calls.hangup(calls.myCall!.uuid)}>
                Hang up
              </button>
            </div>
          </div>
        )}
      </div>
      {calls.popup && (
        <div
          className={`call-card call-card--${calls.popup.direction}${
            calls.popup.state === "answered" ? " is-live" : " is-ringing"
          }`}
          role="status"
          aria-live="polite"
        >
          <div className="call-card-glow" aria-hidden />
          <div className="call-card-top">
            <span className="call-card-badge">
              <i />
              {calls.popup.direction === "inbound" ? "Incoming call" : "Outgoing call"}
            </span>
            <button
              type="button"
              className="icon-btn call-card-close"
              aria-label="Dismiss"
              onClick={() => calls.dismissPopup()}
            >
              ×
            </button>
          </div>

          <div className="call-card-body">
            <div className="call-card-avatar-wrap" aria-hidden>
              <span className="call-card-ring call-card-ring--a" />
              <span className="call-card-ring call-card-ring--b" />
              <span className="call-card-ring call-card-ring--c" />
              <div className="call-card-avatar">
                {initials(calls.popup.callerName || calls.popup.callerPhone || "C") || "C"}
              </div>
            </div>
            <div className="call-card-wave" aria-hidden>
              <span /><span /><span /><span /><span />
            </div>
            <h3 className="call-card-name">
              {calls.popup.callerName || "Unknown caller"}
            </h3>
            <p className="call-card-phone">
              {calls.popup.callerPhone || calls.popup.to || calls.popup.from}
            </p>
            {calls.popup.callerCompany ? (
              <p className="call-card-company">{calls.popup.callerCompany}</p>
            ) : null}
            <p className="call-card-meta">
              <span className={`pill ${calls.popup.state === "answered" ? "live" : "ring"}`}>
                {calls.popup.state === "answered"
                  ? "On call"
                  : calls.popup.direction === "inbound"
                    ? "Ringing"
                    : "Connecting"}
              </span>
              <span className="call-card-ext">Ext {calls.popup.from || calls.extension}</span>
            </p>
            {calls.myCall ? (
              <p className="call-card-timer">{fmtDurationLive(calls.myCall.duration)}</p>
            ) : null}
            {calls.popup.lastCall ? (
              <div className="call-card-last">
                <div className="call-card-last-head">
                  <span>Last call on this number</span>
                  <b>{timeAgo(String(calls.popup.lastCall.at))}</b>
                </div>
                <p className="call-card-last-when">
                  {formatCallClock(String(calls.popup.lastCall.at))}
                </p>
                <p>
                  {calls.popup.lastCall.direction === "inbound" ? "Inbound" : "Outbound"}
                  {" · "}
                  {lastLabel(
                    String(calls.popup.lastCall.state || ""),
                    String(calls.popup.lastCall.hangupCause || ""),
                  )}
                  {" · "}
                  {fmtTalk(Number(calls.popup.lastCall.duration) || 0)}
                </p>
              </div>
            ) : (
              <div className="call-card-last call-card-last--empty">
                <span>Last call on this number</span>
                <p className="muted">No previous calls found</p>
              </div>
            )}
          </div>

          <div className="call-card-actions">
            {calls.myCall ? (
              <button
                className="btn danger call-card-hangup"
                type="button"
                onClick={() => void calls.hangup(calls.myCall!.uuid)}
              >
                Hang up
              </button>
            ) : (
              <button
                className="btn ghost call-card-hangup"
                type="button"
                onClick={() => calls.dismissPopup()}
              >
                Dismiss
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function AppLayout() {
  return (
    <SoftphoneProvider>
      <CallProvider>
        <Shell />
      </CallProvider>
    </SoftphoneProvider>
  );
}
